import React, { useState, useContext, useEffect } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import ProfileContent from 'components/Profile/ProfileContent';
import { Grid, Modal } from '@mui/material';
import BillingOverview from 'pages/billing/BillingOverview';
import TeamManagement from 'pages/teams/TeamManagement';
import SubscriptionPricing from 'pages/credit-plans/SubscriptionPricing';
import LicenseCentricAssigner from 'pages/assign/LicenseCentricAssigner';
import UsersAPI from 'api/UsersAPI';
import { useSnackbar } from 'notistack';
import KeycloakService from 'services/KeycloakService';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';

const PROFILE_TABS = ['Profile', 'Team', 'Subscriptions', 'Assign', 'Billing'];

const ProfileHome: React.FC = (props: any) => {
  const { cur_user, theme, toggleTheme, refreshSelectedTeamLicenseState } =
    useContext(GlobalContext);
  const isRestricted = cur_user?.role === 'domain-restricted';
  const { enqueueSnackbar } = useSnackbar();
  const getMfaEnabledFromUser = () => {
    const basics = cur_user?.basics as any;
    return Boolean(basics?.totp ?? basics?.attributes?.totp);
  };
  const billingHidden =
    cur_user?.selected_team?.attributes?.billing_hidden?.[0] === 'true';
  const [activeTab, setActiveTab] = useState<string>('');
  const [customerId, setCustomerId] = useState<any>('');
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isMfaEnabled, setIsMfaEnabled] = useState(getMfaEnabledFromUser());
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isTogglingMfa, setIsTogglingMfa] = useState(false);

  const getMatchedCusId = () => {
    cur_user.stripe_cus_info.map((cus: any) => {
      if (cus.name === cur_user.selected_team.name) {
        setCustomerId(cus.id);
      }
    });
  };

  useEffect(() => {
    getMatchedCusId();
  }, []);

  useEffect(() => {
    setIsMfaEnabled(getMfaEnabledFromUser());
  }, [cur_user?.basics]);

  useEffect(() => {
    if (billingHidden && activeTab === 'Billing') {
      setActiveTab('Profile');
      window.sessionStorage.setItem('activeTab', 'Profile');
    }
  }, [billingHidden, activeTab]);

  const handleTabChange = (tab: string) => {
    switch (tab) {
      case 'Profile':
        setActiveTab('Profile');
        window.sessionStorage.setItem('activeTab', 'Profile');
        break;
      case 'Team':
        setActiveTab('Team');
        window.sessionStorage.setItem('activeTab', 'Team');
        break;
      case 'Subscriptions':
        setActiveTab('Subscriptions');
        window.sessionStorage.setItem('activeTab', 'Subscriptions');
        break;
      case 'Assign':
        setActiveTab('Assign');
        window.sessionStorage.setItem('activeTab', 'Assign');
        break;
      case 'Billing':
        if (billingHidden) {
          setActiveTab('Profile');
          break;
        }
        setActiveTab('Billing');
        window.sessionStorage.setItem('activeTab', 'Billing');
        break;
      default:
        setActiveTab('Profile');
    }
  };

  useEffect(() => {
    // Force restricted users to ONLY see the Profile tab
    if (isRestricted) {
      setActiveTab('Profile');
      window.sessionStorage.setItem('activeTab', 'Profile');
      return;
    }
    // Standard flow for normal users. A tab can also come from the URL
    // (`/profile?tab=Subscriptions`), for links from outside the app.
    const queryTab = new URLSearchParams(props.location.search).get('tab');
    const linkTab =
      props.location.state?.linkTab || (queryTab && PROFILE_TABS.includes(queryTab) ? queryTab : null);
    if (linkTab) {
      setActiveTab(linkTab);
      window.sessionStorage.setItem('activeTab', linkTab);
    } else {
      const initialTab = window.sessionStorage.getItem('activeTab');
      setActiveTab(initialTab || 'Profile');
    }
  }, [cur_user?.role, props.location.state?.linkTab, props.location.search]);

  const handleResetPassword = async () => {
    if (isResettingPassword) return;

    const userId = cur_user?.basics?.id;
    if (!userId) {
      enqueueSnackbar('Unable to reset password for this user', {
        variant: 'error',
      });
      return;
    }

    setIsResettingPassword(true);
    const response = await UsersAPI.sendPasswordResetEmail(userId);
    setIsResettingPassword(false);
    if (response?.success) {
      enqueueSnackbar('Password reset email sent', {
        variant: 'success',
      });
      return;
    }

    enqueueSnackbar(
      `Failed to send password reset email: ${response?.result}`,
      {
        variant: 'error',
      },
    );
  };

  const handleToggleMfa = async () => {
    if (isTogglingMfa) return;

    const userId = cur_user?.basics?.id;
    if (!userId) {
      enqueueSnackbar('Unable to update MFA for this user', {
        variant: 'error',
      });
      return;
    }

    setIsTogglingMfa(true);
    if (isMfaEnabled) {
      const disableResponse = await UsersAPI.disableMfa(userId);
      setIsTogglingMfa(false);
      if (disableResponse?.success) {
        setIsMfaEnabled(false);
        enqueueSnackbar('MFA disabled', {
          variant: 'success',
        });
        return;
      }

      enqueueSnackbar(`Failed to disable MFA: ${disableResponse?.result}`, {
        variant: 'error',
      });
      return;
    }

    const response = await UsersAPI.sendMfaSetupEmail(userId);
    setIsTogglingMfa(false);
    if (response?.success) {
      enqueueSnackbar('MFA setup email sent', {
        variant: 'success',
      });
      return;
    }

    enqueueSnackbar(`Failed to enable MFA: ${response?.result}`, {
      variant: 'error',
    });
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) return;

    const userId = cur_user?.basics?.id;
    const currentTeamId = cur_user?.selected_team?.id;
    if (!userId || !currentTeamId) {
      enqueueSnackbar('Unable to delete this account', {
        variant: 'error',
      });
      return;
    }

    setIsDeletingAccount(true);
    const response = await UsersAPI.deleteOwnAccount(userId, currentTeamId);
    setIsDeletingAccount(false);
    if (response?.success) {
      setDeleteAccountModalOpen(false);
      enqueueSnackbar(
        response?.result?.message || 'Removed from current team',
        {
          variant: 'success',
        },
      );
      if (
        response?.result?.data?.keycloakDeleted ||
        (cur_user?.teams?.length || 0) <= 1
      ) {
        KeycloakService.doLogout();
        return;
      }
      window.location.reload();
      return;
    }

    enqueueSnackbar(`Failed to delete account: ${response?.result}`, {
      variant: 'error',
    });
  };

  const handleGoToPlansFromDeleteModal = () => {
    setDeleteAccountModalOpen(false);
    handleTabChange('Plans');
  };

  return (
    <section
      className='home-content-body Home-HeaderDown'
      style={{ minHeight: 0 }}
    >
      <Grid container>
        <Grid item xs={12}>
          <div className='module-header'>
            <div className='module-header-breadcrumbs'>
              <h1 className='analyzers-page-title'>Account</h1>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sx={{ padding: '10px 25px 24px 25px' }}>
          <div className='screening-tabs-container'>
            <div className='screening-tabs-inner'>
              <div
                className={`screening-tab-btn${
                  activeTab === 'Profile' ? '-selected' : ''
                }`}
                onClick={() => handleTabChange('Profile')}
              >
                {activeTab === 'Profile' ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M15 15.75C15 14.7033 15 14.18 14.8708 13.7541C14.58 12.7953 13.8297 12.045 12.8709 11.7542C12.445 11.625 11.9217 11.625 10.875 11.625H7.125C6.07833 11.625 5.55499 11.625 5.12914 11.7542C4.17034 12.045 3.42003 12.7953 3.12918 13.7541C3 14.18 3 14.7033 3 15.75M12.375 5.625C12.375 7.48896 10.864 9 9 9C7.13604 9 5.625 7.48896 5.625 5.625C5.625 3.76104 7.13604 2.25 9 2.25C10.864 2.25 12.375 3.76104 12.375 5.625Z'
                      stroke='#4895EF'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M15 15.75C15 14.7033 15 14.18 14.8708 13.7541C14.58 12.7953 13.8297 12.045 12.8709 11.7542C12.445 11.625 11.9217 11.625 10.875 11.625H7.125C6.07833 11.625 5.55499 11.625 5.12914 11.7542C4.17034 12.045 3.42003 12.7953 3.12918 13.7541C3 14.18 3 14.7033 3 15.75M12.375 5.625C12.375 7.48896 10.864 9 9 9C7.13604 9 5.625 7.48896 5.625 5.625C5.625 3.76104 7.13604 2.25 9 2.25C10.864 2.25 12.375 3.76104 12.375 5.625Z'
                      stroke='var(--card-text)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
                <div
                  className={`screening-tab-btn-text${
                    activeTab === 'Profile' ? '-selected' : ''
                  }`}
                >
                  Profile
                </div>
              </div>
              {!isRestricted && (
                <>
                  <div
                    className={`screening-tab-btn${
                      activeTab === 'Team' ? '-selected' : ''
                    }`}
                    onClick={() => handleTabChange('Team')}
                  >
                    {activeTab === 'Team' ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M8.00001 14L7.93331 13.8999C7.47021 13.2053 7.23867 12.858 6.93274 12.6065C6.66192 12.3839 6.34985 12.2169 6.01441 12.1151C5.63551 12 5.21808 12 4.38323 12H3.46668C2.71994 12 2.34657 12 2.06136 11.8547C1.81047 11.7268 1.6065 11.5229 1.47867 11.272C1.33334 10.9868 1.33334 10.6134 1.33334 9.86667V4.13333C1.33334 3.3866 1.33334 3.01323 1.47867 2.72801C1.6065 2.47713 1.81047 2.27316 2.06136 2.14532C2.34657 2 2.71994 2 3.46668 2H3.73334C5.22682 2 5.97355 2 6.54398 2.29065C7.04575 2.54631 7.4537 2.95426 7.70936 3.45603C8.00001 4.02646 8.00001 4.77319 8.00001 6.26667M8.00001 14V6.26667M8.00001 14L8.06671 13.8999C8.52981 13.2053 8.76136 12.858 9.06728 12.6065C9.33811 12.3839 9.65017 12.2169 9.98561 12.1151C10.3645 12 10.7819 12 11.6168 12H12.5333C13.2801 12 13.6534 12 13.9387 11.8547C14.1895 11.7268 14.3935 11.5229 14.5214 11.272C14.6667 10.9868 14.6667 10.6134 14.6667 9.86667V4.13333C14.6667 3.3866 14.6667 3.01323 14.5214 2.72801C14.3935 2.47713 14.1895 2.27316 13.9387 2.14532C13.6534 2 13.2801 2 12.5333 2H12.2667C10.7732 2 10.0265 2 9.45604 2.29065C8.95427 2.54631 8.54632 2.95426 8.29066 3.45603C8.00001 4.02646 8.00001 4.77319 8.00001 6.26667'
                          stroke='#4895EF'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M8.00001 14L7.93331 13.8999C7.47021 13.2053 7.23867 12.858 6.93274 12.6065C6.66192 12.3839 6.34985 12.2169 6.01441 12.1151C5.63551 12 5.21808 12 4.38323 12H3.46668C2.71994 12 2.34657 12 2.06136 11.8547C1.81047 11.7268 1.6065 11.5229 1.47867 11.272C1.33334 10.9868 1.33334 10.6134 1.33334 9.86667V4.13333C1.33334 3.3866 1.33334 3.01323 1.47867 2.72801C1.6065 2.47713 1.81047 2.27316 2.06136 2.14532C2.34657 2 2.71994 2 3.46668 2H3.73334C5.22682 2 5.97355 2 6.54398 2.29065C7.04575 2.54631 7.4537 2.95426 7.70936 3.45603C8.00001 4.02646 8.00001 4.77319 8.00001 6.26667M8.00001 14V6.26667M8.00001 14L8.06671 13.8999C8.52981 13.2053 8.76136 12.858 9.06728 12.6065C9.33811 12.3839 9.65017 12.2169 9.98561 12.1151C10.3645 12 10.7819 12 11.6168 12H12.5333C13.2801 12 13.6534 12 13.9387 11.8547C14.1895 11.7268 14.3935 11.5229 14.5214 11.272C14.6667 10.9868 14.6667 10.6134 14.6667 9.86667V4.13333C14.6667 3.3866 14.6667 3.01323 14.5214 2.72801C14.3935 2.47713 14.1895 2.27316 13.9387 2.14532C13.6534 2 13.2801 2 12.5333 2H12.2667C10.7732 2 10.0265 2 9.45604 2.29065C8.95427 2.54631 8.54632 2.95426 8.29066 3.45603C8.00001 4.02646 8.00001 4.77319 8.00001 6.26667'
                          stroke='var(--card-text)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                    <div
                      className={`screening-tab-btn-text${
                        activeTab === 'Team' ? '-selected' : ''
                      }`}
                    >
                      Team
                    </div>
                  </div>
                  <div
                    className={`screening-tab-btn${
                      activeTab === 'Subscriptions' ? '-selected' : ''
                    }`}
                    onClick={() => handleTabChange('Subscriptions')}
                  >
                    {activeTab === 'Subscriptions' ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        viewBox='0 0 18 18'
                        fill='none'
                      >
                        <g clipPath='url(#clip0_4640_25133)'>
                          <path
                            d='M11.9533 11.9533C14.5202 11.6096 16.5 9.41101 16.5 6.75C16.5 3.8505 14.1495 1.5 11.25 1.5C8.58899 1.5 6.39037 3.47975 6.0467 6.0467M12 11.25C12 14.1495 9.6495 16.5 6.75 16.5C3.8505 16.5 1.5 14.1495 1.5 11.25C1.5 8.3505 3.8505 6 6.75 6C9.6495 6 12 8.3505 12 11.25Z'
                            stroke='#4895EF'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_4640_25133'>
                            <rect width='18' height='18' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        viewBox='0 0 18 18'
                        fill='none'
                      >
                        <g clipPath='url(#clip0_4640_24978)'>
                          <path
                            d='M11.9533 11.9533C14.5202 11.6096 16.5 9.41101 16.5 6.75C16.5 3.8505 14.1495 1.5 11.25 1.5C8.58899 1.5 6.39037 3.47975 6.0467 6.0467M12 11.25C12 14.1495 9.6495 16.5 6.75 16.5C3.8505 16.5 1.5 14.1495 1.5 11.25C1.5 8.3505 3.8505 6 6.75 6C9.6495 6 12 8.3505 12 11.25Z'
                            stroke='var(--card-text)'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_4640_24978'>
                            <rect width='18' height='18' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                    <div
                      className={`screening-tab-btn-text${
                        activeTab === 'Subscriptions' ? '-selected' : ''
                      }`}
                    >
                      Subscriptions
                    </div>
                  </div>
                  {!isRestricted && (
                    <div
                      className={`screening-tab-btn${
                        activeTab === 'Assign' ? '-selected' : ''
                      }`}
                      onClick={() => handleTabChange('Assign')}
                    >
                      {activeTab === 'Assign' ? (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='hsl(210, 100%, 55%)'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          {...props}
                        >
                          <circle cx='12' cy='12' r='10' />
                          <circle cx='12' cy='12' r='6' />
                          <circle cx='12' cy='12' r='2' />
                        </svg>
                      ) : (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          {...props}
                        >
                          <circle cx='12' cy='12' r='10' />
                          <circle cx='12' cy='12' r='6' />
                          <circle cx='12' cy='12' r='2' />
                        </svg>
                      )}
                      <div
                        className={`screening-tab-btn-text${
                          activeTab === 'Assign' ? '-selected' : ''
                        }`}
                      >
                        Licenses & Credits
                      </div>
                    </div>
                  )}
                  {!billingHidden &&
                  (cur_user.selected_team?.attributes?.team_admin?.includes(
                    cur_user.basics?.email,
                  ) ||
                    cur_user.role === 'owner') ? (
                    <div
                      className={`screening-tab-btn${
                        activeTab === 'Billing' ? '-selected' : ''
                      }`}
                      onClick={() => handleTabChange('Billing')}
                    >
                      {activeTab === 'Billing' ? (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='none'
                        >
                          <path
                            d='M16.5 7.5H1.5M1.5 6.15L1.5 11.85C1.5 12.6901 1.5 13.1101 1.66349 13.431C1.8073 13.7132 2.03677 13.9427 2.31901 14.0865C2.63988 14.25 3.05992 14.25 3.9 14.25L14.1 14.25C14.9401 14.25 15.3601 14.25 15.681 14.0865C15.9632 13.9427 16.1927 13.7132 16.3365 13.431C16.5 13.1101 16.5 12.6901 16.5 11.85V6.15C16.5 5.30992 16.5 4.88988 16.3365 4.56902C16.1927 4.28677 15.9632 4.0573 15.681 3.91349C15.3601 3.75 14.9401 3.75 14.1 3.75L3.9 3.75C3.05992 3.75 2.63988 3.75 2.31901 3.91349C2.03677 4.0573 1.8073 4.28677 1.66349 4.56901C1.5 4.88988 1.5 5.30992 1.5 6.15Z'
                            stroke='#4895EF'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='none'
                        >
                          <path
                            d='M16.5 7.5H1.5M1.5 6.15L1.5 11.85C1.5 12.6901 1.5 13.1101 1.66349 13.431C1.8073 13.7132 2.03677 13.9427 2.31901 14.0865C2.63988 14.25 3.05992 14.25 3.9 14.25L14.1 14.25C14.9401 14.25 15.3601 14.25 15.681 14.0865C15.9632 13.9427 16.1927 13.7132 16.3365 13.431C16.5 13.1101 16.5 12.6901 16.5 11.85V6.15C16.5 5.30992 16.5 4.88988 16.3365 4.56902C16.1927 4.28677 15.9632 4.0573 15.681 3.91349C15.3601 3.75 14.9401 3.75 14.1 3.75L3.9 3.75C3.05992 3.75 2.63988 3.75 2.31901 3.91349C2.03677 4.0573 1.8073 4.28677 1.66349 4.56901C1.5 4.88988 1.5 5.30992 1.5 6.15Z'
                            stroke='var(--card-text)'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                      <div
                        className={`screening-tab-btn-text${
                          activeTab === 'Billing' ? '-selected' : ''
                        }`}
                      >
                        Billing
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </div>
          </div>
        </Grid>
        <div className='HomeDashboard-container'>
          {activeTab === 'Profile' && (
            <ProfileContent
              curUser={cur_user}
              theme={theme}
              toggleTheme={toggleTheme}
              onResetPassword={handleResetPassword}
              onEnableMfa={handleToggleMfa}
              onDeleteAccount={() => setDeleteAccountModalOpen(true)}
              mfaEnabled={isMfaEnabled}
              isResettingPassword={isResettingPassword}
              isTogglingMfa={isTogglingMfa}
            />
          )}
          {activeTab === 'Team' && (
            <TeamManagement
              cusId={customerId}
              cur_user={cur_user}
              handleTabChange={handleTabChange}
            />
          )}
          {activeTab === 'Subscriptions' && (
            <SubscriptionPricing handleTabChange={handleTabChange} />
          )}
          {activeTab === 'Assign' && (
            <LicenseCentricAssigner
              cur_user={cur_user}
              team={cur_user.selected_team}
              handleTabChange={handleTabChange}
              refreshSelectedTeamLicenseState={refreshSelectedTeamLicenseState}
            />
          )}
          {activeTab === 'Billing' && !billingHidden && (
            <BillingOverview
              cusId={customerId}
              cur_user={cur_user}
              team={cur_user.selected_team}
              handleTabChange={handleTabChange}
            />
          )}
        </div>
      </Grid>
      <Modal
        open={deleteAccountModalOpen}
        onClose={() => setDeleteAccountModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setDeleteAccountModalOpen(false)}
          title='Delete Account'
          paragraph={
            <>
              Are you sure? You will lose all your work. This action cannot be
              undone.
              <br />
              <br />
              If you prefer to keep your work and just stop paying for a license
              please{' '}
              <span
                style={{ color: '#4895EF', cursor: 'pointer' }}
                onClick={handleGoToPlansFromDeleteModal}
              >
                click here
              </span>{' '}
            </>
          }
          handleDelete={handleDeleteAccount}
          isLoading={isDeletingAccount}
        />
      </Modal>
    </section>
  );
};

export default ProfileHome;
